import { useCallback, useState } from 'react'
import dynamic from 'next/dynamic'
import Router from 'next/router'
import { VIEWS as AUTH_VIEWS } from '../../containers/auth/modals'
import { useAuth } from '../../contexts/auth'
import Hero from '../../components/hero'
import NextArrowTransparent from '../../components/svg/next-arrow-transparent'
import PageTitle from '../../components/page-title'
import { HeroSearch } from '../../components/inputs'
import { Button } from '../../components/button'
import Loader from '../../components/loader'
import FindVehicleModal, { VIEWS } from './modals/find-vehicle'
import VinInstruction from './modals/vin-instruction'
import { useDeviceType } from '../../contexts/device-type'
import { DeviceType } from '../../constants'
import { Errors, useVinSearch } from './hooks/use-vin-search'
import useMyVehicles from '../../hooks/use-my-vehicles'
import SelectListingType from './modals/select-listing-type-modal'
import { checkBarCodePermissions } from '../../utils/permissions'
import NoticeAboutConciergeModal from './selling-options/components/modals/notice-about-concierge'

const BarcodePicker = dynamic(() => import('./barcode-picker'), {
  ssr: false,
})

export default function SellHero() {
  const [{ isAuthenticated }, { setAuthModal }] = useAuth()

  const [vinInstructionModalOpen, setVinInstructionModalOpen] = useState(false)
  const [findVehicleModalOpen, setFindVehicleModalOpen] = useState(false)
  const [noticeAboutConciergeModal, setNoticeAboutConciergeModal] = useState({
    open: false,
    listingTypeSelected: false,
  })
  const [defaultModalView, setDefaultModalView] = useState(VIEWS.Result)
  const [vin, setVin] = useState('')
  const [listingType, setListingType] = useState(null)
  const [selectListingType, setSelectListingType] = useState({
    open: false,
    onSelect: () => {},
  })

  const [enableScanning, setEnableScanning] = useState(false)
  const [scanSettings, setScanSettings] = useState()
  const deviceType = useDeviceType()
  const isMobile = [DeviceType.Mobile, DeviceType.Tablet].includes(deviceType)

  const { search, isLoading, error, vehicle } = useVinSearch({
    onSuccess: () => {
      setNoticeAboutConciergeModal({ open: true, listingTypeSelected: false })
    },
    onError: (error) => {
      if (error === Errors.VEHICLE_BY_VIN_NOT_FOUND) {
        setSelectListingType({
          open: true,
          onSelect: (listingType) => {
            setListingType(listingType)
            setDefaultModalView(VIEWS.NotFound)
            setNoticeAboutConciergeModal({ open: true, listingTypeSelected: true })
          },
        })
      }
    },
  })

  const createScanSettings = async () => {
    const { ScanSettings } = await import('scandit-sdk')
    setScanSettings(
      new ScanSettings({
        enabledSymbologies: ['code39'],
        codeDuplicateFilter: 1000,
      }),
    )
    setEnableScanning(true)
  }

  const URL = useMyVehicles(vin)

  const handleSearch = () => {
    if (URL) {
      Router.push(URL)
    } else {
      search(vin)
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (isAuthenticated) {
      handleSearch()
    } else setAuthModal({ open: true, view: AUTH_VIEWS.Login })
  }

  const handleInputChange = (event) => {
    let vin = event.target.value?.toUpperCase()
    setVin(vin)
  }

  const handleScanning = (scanResult) => {
    setEnableScanning(false)

    let barcodeValue = scanResult.barcodes[0].data
    barcodeValue = barcodeValue.trim()

    if (barcodeValue.length === 18 || barcodeValue.length === 19) {
      barcodeValue = barcodeValue.substring(1, 18)
    } else if (barcodeValue.length === 20) {
      barcodeValue = barcodeValue.substring(2, 19)
    }

    setVin(barcodeValue)
  }

  const handleModalClose = useCallback(() => {
    setFindVehicleModalOpen(false)
    setDefaultModalView(VIEWS.Result)
  }, [])

  const handleManualEntry = (event) => {
    event.preventDefault()

    if (isAuthenticated) {
      setSelectListingType({
        open: true,
        onSelect: (listingType) => {
          setListingType(listingType)
          setDefaultModalView(VIEWS.ManualEntry)
          setNoticeAboutConciergeModal({ open: true, listingTypeSelected: true })
        },
      })
    } else {
      setAuthModal({ open: true, view: AUTH_VIEWS.Login })
    }
  }

  const handleSelectListingTypeClose = () => {
    setSelectListingType({ open: false, onSelect: () => {} })
  }

  const handleAcceptConciergeCondition = () => {
    setNoticeAboutConciergeModal({ open: false, listingTypeSelected: false })

    if (noticeAboutConciergeModal.listingTypeSelected) {
      setSelectListingType({ open: false, onSelect: () => {} })
      setFindVehicleModalOpen(true)
    } else {
      setSelectListingType({
        open: true,
        onSelect: (listingType) => {
          setListingType(listingType)
          setSelectListingType({ open: false, onSelect: () => {} })
          setDefaultModalView(VIEWS.Result)
          setFindVehicleModalOpen(true)
        },
      })
    }
  }

  const heroSearchButtonSection =
    checkBarCodePermissions() && isMobile ? (
      <Button onClick={createScanSettings}>Scan VIN</Button>
    ) : (
      <Button type='submit' disabled={isLoading} size='lg'>
        Get Started
      </Button>
    )

  return (
    <>
      {isLoading && <Loader delay='400' fullPage />}

      {findVehicleModalOpen && (
        <FindVehicleModal
          listingType={listingType}
          defaultView={defaultModalView}
          vehicle={vehicle}
          onClose={handleModalClose}
        />
      )}

      {noticeAboutConciergeModal.open && (
        <NoticeAboutConciergeModal
          onAccept={handleAcceptConciergeCondition}
          onClose={() => setNoticeAboutConciergeModal({ open: false, listingTypeSelected: false })}
        />
      )}

      {vinInstructionModalOpen && (
        <VinInstruction onClose={() => setVinInstructionModalOpen(false)} />
      )}

      {selectListingType.open && (
        <SelectListingType
          onClose={handleSelectListingTypeClose}
          onSelect={selectListingType.onSelect}
        />
      )}

      {enableScanning && checkBarCodePermissions() && (
        <BarcodePicker
          playSoundOnScan={true}
          vibrateOnScan={true}
          visible={enableScanning}
          scanSettings={scanSettings}
          onScan={(scanResult) => handleScanning(scanResult)}
          onError={(error) => {
            alert(error.message)
            setEnableScanning(false)
          }}
          onClose={() => setEnableScanning(false)}
        />
      )}

      <Hero desktopImageSrc='/images/heroes/sell.jpg' opacity={1}>
        <div className='container'>
          <PageTitle>
            <label htmlFor='vin'>
              Before You Sell to Anyone...
              <br />
              Try Selling to <i>Everyone</i>
              <br />
              Fast, Easy and FREE
            </label>
          </PageTitle>
          {isMobile ? (
            <div className='search-container'>
              <form onSubmit={handleSubmit}>
                {heroSearchButtonSection}
                <div className='additionalButtons'>
                  <button className='flatBtn' onClick={() => setVinInstructionModalOpen(true)}>
                    Help me find my VIN.
                  </button>
                </div>
                <HeroSearch
                  id='vin'
                  name='vin'
                  autoComplete='vin'
                  placeholder={isMobile ? 'Manually enter VIN' : 'Enter Vehicle VIN'}
                  adornment={isMobile ? <NextArrowTransparent onClick={handleSubmit} /> : null}
                  maxLength={17}
                  disabled={isLoading}
                  error={[
                    Errors.VIN_IS_NOT_VALID,
                    Errors.EMPTY_FIELD,
                    Errors.CANNOT_LOCATE_YOUR_VIN,
                  ].includes(error)}
                  value={vin}
                  onChange={handleInputChange}
                  data-hj-allow
                />
                {error === Errors.VIN_IS_NOT_VALID && (
                  <div className='error'>Vin appears to be invalid, please try again.</div>
                )}
                {error === Errors.EMPTY_FIELD && (
                  <div className='error'>Please, enter VIN number</div>
                )}
                {error === Errors.CANNOT_LOCATE_YOUR_VIN && (
                  <div className='flex flexDirectionColumn alignCenter'>
                    <div className='error'>
                      We still cannot locate your VIN. If this is your correct VIN, please use the
                      manual entry form to add your vehicle
                    </div>
                    <button className='flatBtn enterDetails' onClick={handleManualEntry}>
                      Enter vehicle details
                    </button>
                  </div>
                )}
              </form>
              <div className='additionalButtons'>
                <button className='flatBtn' onClick={handleManualEntry}>
                  No VIN? Click here to enter your vehicle manually
                </button>
              </div>
            </div>
          ) : (
            <div className='search-container'>
              <form onSubmit={handleSubmit}>
                <HeroSearch
                  id='vin'
                  name='vin'
                  autoComplete='vin'
                  placeholder={isMobile ? 'Manually enter VIN' : 'Enter Vehicle VIN'}
                  adornment={isMobile ? <NextArrowTransparent onClick={handleSubmit} /> : null}
                  maxLength={17}
                  disabled={isLoading}
                  error={[
                    Errors.VIN_IS_NOT_VALID,
                    Errors.EMPTY_FIELD,
                    Errors.CANNOT_LOCATE_YOUR_VIN,
                  ].includes(error)}
                  value={vin}
                  onChange={handleInputChange}
                  data-hj-allow
                />
                {error === Errors.VIN_IS_NOT_VALID && (
                  <div className='error'>Vin appears to be invalid, please try again.</div>
                )}
                {error === Errors.EMPTY_FIELD && (
                  <div className='error'>Please, enter VIN number</div>
                )}
                {error === Errors.CANNOT_LOCATE_YOUR_VIN && (
                  <div className='flex flexDirectionColumn alignCenter'>
                    <div className='error'>
                      We still cannot locate your VIN. If this is your correct VIN, please use the
                      manual entry form to add your vehicle
                    </div>
                    <button className='flatBtn enterDetails' onClick={handleManualEntry}>
                      Enter vehicle details
                    </button>
                  </div>
                )}
                {heroSearchButtonSection}
              </form>
              <div className='additionalButtons'>
                <button className='flatBtn' onClick={handleManualEntry}>
                  No VIN? Click here to enter your vehicle manually
                </button>
                <span> or </span>
                <button className='flatBtn' onClick={() => setVinInstructionModalOpen(true)}>
                  Help me find my VIN.
                </button>
              </div>
            </div>
          )}
        </div>
      </Hero>

      <style jsx>{`
        .container {
          max-width: 77.2rem;
          width: 100%;
          margin: 0 auto;

          :global(.pageTitle) {
            text-align: center;

            label {
              display: block;
              padding: 0 0 2.1rem;
              color: var(--primary-bg-color);
            }
          }

          form {
            text-align: center;

            :global(button) {
              margin: 2.1rem 0 0;
            }

            :global(.inputContainer svg) {
              width: 4rem;
              height: 4rem;
              pointer-events: unset;
              opacity: 1;
              fill: var(--primary-color);
            }

            .error {
              margin: 1.3rem 0 0;
              text-align: center;
              color: var(--alert-color);
              font-size: 1.8rem;
            }
          }

          .additionalButtons {
            margin: 1rem;
            text-align: center;
            font-size: 1.8rem;

            span {
              color: var(--secondary-text-color);
            }

            a,
            button {
              color: var(--text-link-color);
            }
          }

          .enterDetails {
            margin: 1rem 0 0;
            color: var(--text-link-color);
            font-size: 1.8rem;
          }
        }

        @media (max-width: 1024px) {
          .container {
            max-width: 35rem;
            padding: 0 0 2rem;

            :global(.pageTitle) {
              label {
                padding: 0 0 1.9rem;
              }
            }

            form {
              .error {
                margin: 0.5rem 0 0;
                font-size: 1.4rem;
              }

              :global(button) {
                margin: 1.5rem 0 0;
              }

              :global(.inputContainer svg) {
                width: 2.5rem;
                height: 2.5rem;
                right: 0.5rem;
              }
            }

            .additionalButtons {
              margin: 1rem;
              font-size: 1.4rem;
            }

            .enterDetails {
              margin: 0.6rem 0 0;
              font-size: 1.4rem;
            }
          }
        }
      `}</style>
    </>
  )
}
